import { getTimeZones } from "@vvo/tzdb";

export interface TimezoneOption {
  value: string;
  label: string;
  /** Hidden search terms: the zone's tzdb group aliases (America/Detroit, CET, …). */
  keywords?: string[];
}

// tzdb's list is stable for the life of the page (offsets only shift on a DST
// boundary), so compute it once per session instead of per mount.
let cachedZones: ReturnType<typeof getTimeZones> | null = null;
let cachedOptions: TimezoneOption[] | null = null;

function loadZones() {
  cachedZones ??= getTimeZones({ includeUtc: true });
  return cachedZones;
}

function loadOptions(): TimezoneOption[] {
  cachedOptions ??= loadZones()
    // The IANA name leads the label so search matches it as well as the
    // friendly name and main cities baked into currentTimeFormat. tzdb calls
    // the UTC entry "Etc/UTC"; surface it under the plain "UTC" alias users
    // know — also what this app historically stored. The group aliases ride
    // along as hidden keywords: tzdb canonicalizes ~100 zones the browser
    // still reports (America/Detroit, America/Indiana/*, …) and users must
    // be able to find those by name.
    .map((tz) => {
      const keywords = tz.group.filter((g) => g !== tz.name);
      return tz.name === "Etc/UTC"
        ? { value: "UTC", label: "UTC (+00:00 Coordinated Universal Time)", keywords }
        : { value: tz.name, label: `${tz.name} (${tz.currentTimeFormat})`, keywords };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
  return cachedOptions;
}

/** "America/Detroit" + "+02:00 Eastern Time - New York, …" → "America/Detroit (+02:00 Eastern Time)".
 * The alias keeps the canonical zone's offset and friendly name but not its
 * cities — a Detroit row listing New York's boroughs reads as wrong data. */
function aliasLabel(alias: string, currentTimeFormat: string): string {
  return `${alias} (${currentTimeFormat.split(" - ")[0]})`;
}

/**
 * Full IANA timezone options, with every value in `currentValues` guaranteed
 * selectable. tzdb canonicalizes merged zones (e.g. "Etc/UTC" is surfaced
 * here as "UTC"), so a stored alias — or any unknown legacy string — is
 * prepended as its own option rather than blanking the trigger.
 */
export function buildTimezoneOptions(
  currentValues?: string | readonly string[] | null,
): TimezoneOption[] {
  const options = loadOptions();
  const values = (typeof currentValues === "string" ? [currentValues] : currentValues ?? []).filter(
    (v) => v && !options.some((o) => o.value === v),
  );
  if (values.length === 0) {
    return options;
  }
  const extras = values.map((value) => {
    const groupMatch = loadZones().find((tz) => tz.group.includes(value));
    return {
      value,
      label: groupMatch ? aliasLabel(value, groupMatch.currentTimeFormat) : value,
    };
  });
  return [...extras, ...options];
}

export function getBrowserTimeZone(): string {
  try {
    // Matches the organizations.timezone column default when the browser
    // reports nothing usable.
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Brussels";
  } catch {
    return "Europe/Brussels";
  }
}

/**
 * Search filter for timezone options. Two jobs cmdk's default filter gets
 * wrong for this option set:
 * - matching: fuzzy subsequences are far too loose here — "utc" matched a
 *   third of all zones (any label containing u…t…c in order, e.g.
 *   "Ulaanbaatar … Choibalsan"). Every whitespace token must instead appear
 *   as a contiguous, case-insensitive substring of the label or a keyword.
 * - ranking: a flat score would highlight the alphabetically-first match, so
 *   "amsterdam" would select America/Guyana (city "New Amsterdam") over
 *   Europe/Amsterdam. Hits on the IANA name outrank alias hits, which
 *   outrank hits in the friendly-name/cities part of the label.
 */
export function timezoneSearchFilter(label: string, search: string, keywords?: string[]): number {
  const s = search.trim().toLowerCase();
  if (!s) return 1;
  const lowerLabel = label.toLowerCase();
  const lowerKeywords = (keywords ?? []).map((k) => k.toLowerCase());
  const haystack = [lowerLabel, ...lowerKeywords].join(" ");
  if (!s.split(/\s+/).every((token) => haystack.includes(token))) {
    return 0;
  }
  const name = lowerLabel.split(" (")[0];
  if (name === s) return 10;
  if (name.startsWith(s)) return 8;
  if (name.includes(s)) return 6;
  if (lowerKeywords.some((k) => k.includes(s))) return 5;
  if (lowerLabel.includes(s)) return 4;
  return 1;
}
